import React, {
  useCallback, useId, useLayoutEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';

import Pagination from 'components/DesignSystem/Pagination';
import { getPageCount } from 'components/DesignSystem/Pagination/pageWindow';
import Toast from 'components/DesignSystem/Toast';
import AutoRefreshIndicator from 'components/Flux/Personalized/MyStudies/AutoRefreshIndicator';
import FilterBar from 'components/Flux/Personalized/MyStudies/FilterBar';
import CompanyFilter from 'components/Flux/Personalized/MyStudies/FilterBar/CompanyFilter';
import UserPopover from 'components/Flux/Personalized/MyStudies/FilterBar/CreatorFilter/UserPopover';
import PeriodFilter from 'components/Flux/Personalized/MyStudies/FilterBar/PeriodFilter';
import FilterSummary from 'components/Flux/Personalized/MyStudies/FilterSummary';
import {
  MyStudiesProduct, MyStudiesProductContext, PERSONALIZED_PRODUCT, useMyStudiesProduct,
} from 'components/Flux/Personalized/MyStudies/product';
import ListZone from 'components/Flux/Personalized/MyStudies/ListZone';
import MigrationNotice from 'components/Flux/Personalized/MyStudies/MigrationNotice';
import ScenariosPanel from 'components/Flux/Personalized/MyStudies/ScenariosPanel';
import TableHeader from 'components/Flux/Personalized/MyStudies/TableHeader';
import useActionFeedback, {
  ACTION_SUCCESS_DURATION_MS,
} from 'components/Flux/Personalized/MyStudies/useActionFeedback';
import { isCompanyWithoutStudies } from 'helper/flux/myStudiesEmptyState';
import { MY_STUDIES_PAGE_SIZES, isMyStudiesPageSize } from 'helper/flux/myStudiesFilters';
import { isValidUserId } from 'helper/flux/myStudyRow';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';
import { meteorologyApi } from 'services/api/Meteorology';
import useMyStudiesFilterLabels from 'utils/hooks/flux/personalized/useMyStudiesFilterLabels';
import useMyStudiesFilters from 'utils/hooks/flux/personalized/useMyStudiesFilters';
import useMyStudiesAutoRefresh from 'utils/hooks/flux/personalized/useMyStudiesAutoRefresh';
import useMyStudiesPage from 'utils/hooks/flux/personalized/useMyStudiesPage';
import useStudiesErrorState from 'utils/hooks/flux/personalized/useStudiesErrorState';
import { MyStudy } from 'types/Flux/Personalized/myStudies';
import './index.scss';

/** Zona da lista (`ListZone`, estilizada em `index.scss`), focável só por programa. */
const LIST_ZONE_SELECTOR = '.my-studies-list';

/** Complemento do "Mostrando a–b de N" (.dc.html:731). */
const PAGINATION_TRAILING_LABEL = 'ordenado por última execução';

/**
 * Referência "Criação do estudo" liberada (D8). Fonte única: o `PeriodFilter` a mostra desabilitada
 * e o `useMyStudiesFilters` troca `ref=created` da URL ou da sessão por `last_exec`. Fica `false`
 * até o backend confirmar o `created_at`; a #2006 liga.
 */
const CREATED_REF_AVAILABLE = false;

/**
 * Casca desktop da nova aba Meus Estudos do Flux Personalizado (#1983), atrás da flag
 * `fluxMeusEstudosV2`. Define as zonas da tela, na ordem do design (zonas vazias ficam ocultas
 * pelo CSS):
 * - `notice`: aviso de migração da caixa "Casos Ampere" (#1997, só admin);
 * - `filters`: barra de filtros (#1995), com o resumo da #1997 (contagem, chips e "Atualizando
 *   lista…") no rodapé dela, como no protótipo, e os filtros Empresa (só admin) e Usuário (#2000)
 *   nos slots dela. Escondida, com a contagem, no estado 4 (#2003: o escopo não tem estudos);
 * - `summary`: reservada e vazia (o resumo foi para o rodapé da barra);
 * - `header`: cabeçalho da lista;
 * - `list`: a `ListZone` (#1999), com o conteúdo de cada estado (#1992), os vazios (#2003) e os
 *   erros das ações; a linha aberta mostra o `ScenariosPanel` (#2002), pelo `renderExpanded`;
 * - `footer`: paginação e itens por página (#1986), só com a lista na tela e total > 0.
 *
 * Os filtros da tela vêm de uma única instância do `useMyStudiesFilters`, criada aqui: a barra,
 * o resumo, os chips, o aviso e a página (`useMyStudiesPage`) recebem `myStudiesFilters` (ou
 * partes dele) por props. Uma segunda instância teria o próprio timer de busca.
 *
 * O hook fica na casca, e não na view, de propósito: ele reescreve a URL e a sessão assim que
 * monta, e a casca só monta com a flag ligada no desktop. É isso que deixa a tela atual idêntica
 * com a flag desligada (e no mobile). Um mobile V2 futuro monta a própria instância (nunca as duas
 * juntas) ou o hook ganha um `enabled`; não suba o hook para a view.
 *
 * O rascunho da busca mora aqui e cada tecla re-renderiza a casca: as linhas são memoizadas e
 * recebem só props estáveis. A busca digitada já esmaece a lista, como o protótipo
 * (.dc.html:615, 726). O sucesso das ações (excluir, cancelar, reenviar) aparece no `Toast` do DS,
 * fora das zonas; erros e avisos ficam na zona da lista até serem fechados (`useActionFeedback`).
 *
 * Atualização automática (#2004): o `useMyStudiesAutoRefresh` monta o polling de status da #2001
 * sobre a MESMA consulta da página e concentra as regras (retry, lista nova com o status em erro,
 * D12, hora do indicador). A casca só liga as peças:
 * - `AutoRefreshIndicator` no slot do resumo, com o relógio de 1 s dentro dele: o tique não
 *   re-renderiza a casca nem as linhas;
 * - a faixa única e o destaque na `ListZone`;
 * - os avisos de pronto na fila única do `useActionFeedback`, no mesmo `Toast` do sucesso das
 *   ações. O `Toast` fica em `bottom-center`, e não no canto inferior direito que a issue pede: lá
 *   fica o `DownloadProgressCard` (fixo, z-index 1090), que o cobriria. A mescla no cache já
 *   habilita o Baixar.
 */
function MyStudiesShell() {
  const { storage, expandable } = useMyStudiesProduct();
  const userId: number = useSelector(selectUserId);
  // O papel já está certo no 1º render: o PersistGate só monta a tela com o auth reidratado
  const isAdmin = useSelector(selectUserIsAdmin) === true;
  const myStudiesFilters = useMyStudiesFilters({
    userId,
    isAdmin,
    createdRefAvailable: CREATED_REF_AVAILABLE,
    storagePrefix: storage.filtersPrefix,
  });
  const {
    filters, searchDraft, setFilters, clearFilters, setPage,
  } = myStudiesFilters;
  // Os filtros só guardam ids: os nomes dos chips vêm das listas de empresas e de usuários (#2000)
  const { companyLabel, userLabel } = useMyStudiesFilterLabels(filters, { userId, isAdmin });

  const sectionRef = useRef<HTMLElement>(null);
  // Sem usuário (sem JWT), a consulta espera o login, como a URL e a sessão dos filtros
  const page = useMyStudiesPage(myStudiesFilters, {
    skip: !isValidUserId(userId),
    scrollAnchorRef: sectionRef,
  });
  const {
    list, state, hasListOnScreen, isBeyondLastPage, nowMs, manualRefreshPending,
  } = page;
  const {
    successMessage, clearSuccess, alert, dismissAlert, showFeedback, showReadyNotices,
  } = useActionFeedback();
  // Polling de status (#2001) e as regras da #2004; a casca só existe no desktop (D14)
  const autoRefresh = useMyStudiesAutoRefresh({
    page, enabled: isValidUserId(userId), onReadyEvents: showReadyNotices,
  });
  const viewer = useMemo(() => ({ userId, isAdmin }), [userId, isAdmin]);

  // O aviso some com o botão que tinha o foco: o foco vai para a busca, não para o <body>
  const searchInputId = useId();
  const focusSearch = useCallback(() => {
    document.getElementById(searchInputId)?.focus();
  }, [searchInputId]);

  const changePerPage = useCallback((perPage: number) => {
    if (isMyStudiesPageSize(perPage)) setFilters({ perPage });
  }, [setFilters]);

  // A busca digitada esperando o debounce também conta: a lista na tela já não é a do campo
  const listPending = state === 'pending' || searchDraft.trim() !== filters.busca;
  // No `error-initial`, o `data` que sobrou é de outro filtro: a contagem vira "—"
  const total = state === 'error-initial' ? undefined : list?.total;
  const pagination = hasListOnScreen && list !== undefined && list.total > 0 && !isBeyondLastPage
    ? list : null;
  // Estado 4 (#2003), calculado só aqui: com a resposta de um escopo sem nenhum estudo na tela,
  // não há o que filtrar, e a barra some. Nunca no skeleton (sem lista na tela). No pending (ex.:
  // voltar no navegador) a barra continua escondida enquanto a `ListZone` mostra o skeleton: o
  // escopo não depende dos filtros
  const companyWithoutStudies = hasListOnScreen && list !== undefined
    && isCompanyWithoutStudies(list, filters, isAdmin);

  // A barra some com o foco nela (ex.: busca digitada durante o skeleton): antes da pintura, o
  // foco vai para a zona da lista, que mostra o estado 4, e não cai no <body>
  const filtersZoneRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!companyWithoutStudies || !filtersZoneRef.current?.contains(document.activeElement)) return;
    const listZone = sectionRef.current?.querySelector<HTMLElement>(LIST_ZONE_SELECTOR);
    listZone?.focus({ preventScroll: true });
  }, [companyWithoutStudies]);

  // Slots da #2000. Empresa só para admin: o cliente nem monta o campo nem pede as empresas.
  // Memoizados: cada tecla na busca re-renderiza a casca, e o mesmo elemento não re-renderiza
  const { empresa, criadoPor } = filters;
  const companySlot = useMemo(() => (isAdmin
    ? <CompanyFilter value={empresa} viewerUserId={userId} onFiltersChange={setFilters} />
    : undefined), [isAdmin, empresa, userId, setFilters]);
  const userSlot = useMemo(() => (
    <UserPopover
      value={criadoPor}
      empresa={empresa}
      isAdmin={isAdmin}
      viewerUserId={userId}
      onFiltersChange={setFilters}
    />
  ), [criadoPor, empresa, isAdmin, userId, setFilters]);

  // Slot do período (#1996), memoizado: cada tecla na busca re-renderiza a casca, e o mesmo
  // elemento não re-renderiza o campo. Dependências primitivas: o `intervalo` é um objeto novo a
  // cada leitura da URL
  const { ref: dateRef, periodo, intervalo: { from: intervalFrom, to: intervalTo } } = filters;
  const periodSlot = useMemo(() => (
    <PeriodFilter
      filters={{ ref: dateRef, periodo, intervalo: { from: intervalFrom, to: intervalTo } }}
      onFiltersChange={setFilters}
      createdRefAvailable={CREATED_REF_AVAILABLE}
    />
  ), [dateRef, periodo, intervalFrom, intervalTo, setFilters]);

  // Painel da linha aberta (#2002): só devolve o componente, que pede os cenários ao montar.
  // Estável (deps vazias): entra na memoização das linhas da StudyList, e cada tecla na busca
  // re-renderiza a casca sem re-renderizar as linhas
  const renderExpanded = useCallback((study: MyStudy) => (
    <ScenariosPanel studyId={study.id} studyName={study.name} />
  ), []);

  // Rótulo do Membro nos painéis abertos (#2002): cada painel assina o `painel-options` do Flux, e
  // a casca só lê a entrada, sem pedir nada. A falha vai ao Sentry (e a sessão expirada, ao login)
  // uma vez, com qualquer número de painéis abertos
  const { useQueryState: useMemberOptionsState } = meteorologyApi.endpoints.controlPanelOptions;
  const { error: memberOptionsError } = useMemberOptionsState('flux');
  useStudiesErrorState(memberOptionsError, { mode: 'auxiliary' });

  return (
    <>
      <section ref={sectionRef} className="my-studies" aria-label="Meus estudos" data-testid="my-studies">
        <div className="my-studies-notice" data-testid="my-studies-notice">
          <MigrationNotice
            isAdmin={isAdmin}
            userId={userId}
            onFiltersChange={setFilters}
            onDismiss={focusSearch}
          />
        </div>
        <div
          ref={filtersZoneRef}
          className="my-studies-filters"
          data-testid="my-studies-filters"
          hidden={companyWithoutStudies}
        >
          <FilterBar
            filters={filters}
            searchDraft={searchDraft}
            onFiltersChange={setFilters}
            isAdmin={isAdmin}
            searchInputId={searchInputId}
            periodSlot={periodSlot}
            footer={(
              // `setFilters` vai direto (sem lambda): o chip removido pede `{ immediate: true }`.
              // `companyLabel` e `userLabel` são os nomes das listas de opções (#2000).
              <FilterSummary
                filters={filters}
                searchDraft={searchDraft}
                total={total}
                isInitialLoading={state === 'initial'}
                isPending={listPending}
                onFiltersChange={setFilters}
                onClearFilters={clearFilters}
                companyLabel={companyLabel}
                userLabel={userLabel}
                updateIndicator={(
                  <AutoRefreshIndicator
                    lastUpdatedAt={autoRefresh.updatedAt}
                    hasActive={autoRefresh.hasActive}
                    hasError={autoRefresh.offline}
                    refreshing={manualRefreshPending}
                    onRefresh={autoRefresh.refresh}
                  />
                )}
              />
            )}
            companySlot={companySlot}
            userSlot={userSlot}
          />
        </div>
        <div className="my-studies-summary" data-testid="my-studies-summary" />
        <div className="my-studies-header" data-testid="my-studies-header">
          <TableHeader />
        </div>
        <ListZone
          state={state}
          list={list}
          hasListOnScreen={hasListOnScreen}
          isBeyondLastPage={isBeyondLastPage}
          pending={listPending}
          viewer={viewer}
          filters={filters}
          userLabel={userLabel}
          onFiltersChange={setFilters}
          onClearFilters={clearFilters}
          companyWithoutStudies={companyWithoutStudies}
          nowMs={nowMs}
          lastUpdatedAt={autoRefresh.staleSince}
          statusFailed={autoRefresh.statusFailed}
          staleBannerKey={autoRefresh.staleBannerKey}
          onRetry={autoRefresh.retryFailed}
          onFeedback={showFeedback}
          actionAlert={alert}
          onDismissActionAlert={dismissAlert}
          onActionSuccess={autoRefresh.onActionSuccess}
          renderExpanded={expandable ? renderExpanded : undefined}
          highlightedIds={autoRefresh.highlightedIds}
        />
        <div className="my-studies-footer" data-testid="my-studies-footer">
          {pagination && (
            <Pagination
              page={Math.min(filters.page, getPageCount(pagination.total, filters.perPage))}
              perPage={filters.perPage}
              total={pagination.total}
              perPageOptions={MY_STUDIES_PAGE_SIZES}
              onPageChange={setPage}
              onPerPageChange={changePerPage}
              disabled={listPending}
              trailingLabel={PAGINATION_TRAILING_LABEL}
            />
          )}
        </div>
      </section>
      {/* Fila única: sucesso das ações e "pronto para download" (#2004), um aviso por vez. No
          centro, o padrão do DS: no canto direito (o que a issue pede), o card de download
          (fixed) o cobriria */}
      <Toast
        message={successMessage}
        onClose={clearSuccess}
        duration={ACTION_SUCCESS_DURATION_MS}
      />
    </>
  );
}

export interface MyStudiesProps {
  /** Produto da aba; padrão: Flux Personalizado (a view do Upload passa `UPLOAD_PRODUCT`). */
  product?: MyStudiesProduct;
}

/** Casca da aba Meus Estudos: fixa o produto no Context e monta a tela (`MyStudiesShell`). */
export default function MyStudies({ product = PERSONALIZED_PRODUCT }: MyStudiesProps) {
  return (
    <MyStudiesProductContext.Provider value={product}>
      <MyStudiesShell key={product.key} />
    </MyStudiesProductContext.Provider>
  );
}
